// =============================================================================
// SEO — canonical URL utilities
// =============================================================================
//
// Single source of truth for canonical URL construction across the
// portal. Used by:
//
//   - sitemap.ts generators (per-tenant)
//   - robots.ts
//   - route generateMetadata() functions (canonical link tags)
//   - Schema.org generators (the `url` and `mainEntityOfPage` fields)
//
// Centralizing here means we can:
//   1. Swap the origin source (env var, runtime config, header) in one place
//   2. Audit URL inconsistencies (trailing slashes, double slashes, etc.)
//   3. Make tests deterministic by mocking ONE function
//
// =============================================================================
// Origin source
// =============================================================================
//
// Three signals, in priority order:
//   1. NEXT_PUBLIC_SITE_ORIGIN env var          — explicit override
//   2. process.env.VERCEL_URL                   — auto-set on Vercel
//   3. http://localhost:3000                    — local dev fallback
//
// Note: VERCEL_URL is hostname only (no protocol), so we prepend https.
// Self-hosted deployments should set NEXT_PUBLIC_SITE_ORIGIN; otherwise
// the localhost fallback ships in URL outputs (visible bug, easy to
// spot in early QA).

export function canonicalOrigin(): string {
  if (process.env.NEXT_PUBLIC_SITE_ORIGIN) {
    return process.env.NEXT_PUBLIC_SITE_ORIGIN.replace(/\/$/, '');
  }
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }
  return 'http://localhost:3000';
}

// =============================================================================
// URL composers
// =============================================================================
//
// Each composer encodes the path's segments. Path arrays are typed as
// (string | number)[] for convenience — every segment gets coerced and
// encoded. Falsy segments are skipped (e.g. an empty courseId param won't
// produce a trailing slash).

function compose(...segments: Array<string | number | null | undefined>): string {
  const path = segments
    .filter((s): s is string | number => s != null && s !== '')
    .map((s) => encodeURIComponent(String(s)))
    .join('/');
  return `${canonicalOrigin()}/${path}`;
}

/** Tenant root: https://example.com/{tenantSlug} */
export function canonicalTenantRoot(tenantSlug: string): string {
  return compose(tenantSlug);
}

/** Tenant CMS page: https://example.com/{tenantSlug}/{pageSlug} */
export function canonicalTenantPage(tenantSlug: string, pageSlug: string): string {
  return compose(tenantSlug, pageSlug);
}

/** Catalog index: https://example.com/{tenantSlug}/catalog */
export function canonicalCatalog(tenantSlug: string): string {
  return compose(tenantSlug, 'catalog');
}

/** Course detail: https://example.com/{tenantSlug}/lxp/course/{title-id} */
export function canonicalCourseDetail(tenantSlug: string, courseId: string): string {
  return compose(tenantSlug, 'lxp', 'course', courseId);
}

/** Video detail: https://example.com/{tenantSlug}/video/{slug} */
export function canonicalVideoDetail(tenantSlug: string, slug: string): string {
  return compose(tenantSlug, 'tube', slug);
}

/** Blog post: https://example.com/{tenantSlug}/blog/{slug} */
export function canonicalBlogPost(tenantSlug: string, slug: string): string {
  return compose(tenantSlug, 'blog', slug);
}

/** Wiki page: https://example.com/{tenantSlug}/wiki/{...slug} */
export function canonicalWikiPage(tenantSlug: string, slugPath: string): string {
  // slugPath may already contain slashes (Next's [...slug] catch-all
  // gives us an array we join with '/'). Encode each segment.
  const encoded = slugPath.split('/').map(encodeURIComponent).join('/');
  return `${canonicalOrigin()}/${encodeURIComponent(tenantSlug)}/wiki/${encoded}`;
}

// =============================================================================
// Index/list URLs (for sitemap entries that aren't detail pages)
// =============================================================================

export function canonicalBlogIndex(tenantSlug: string): string {
  return compose(tenantSlug, 'blog');
}

export function canonicalWikiIndex(tenantSlug: string): string {
  return compose(tenantSlug, 'wiki');
}

export function canonicalVideoIndex(tenantSlug: string): string {
  return compose(tenantSlug, 'tube');
}

// =============================================================================
// Sitemap helpers
// =============================================================================
//
// Used by app/[tenantSlug]/sitemap.ts. Returns the URL path for a
// tenant's sitemap, which the root robots.txt advertises.

export function tenantSitemapUrl(tenantSlug: string): string {
  return `${compose(tenantSlug)}/sitemap.xml`;
}

export function tenantVideoSitemapUrl(tenantSlug: string): string {
  return `${compose(tenantSlug)}/video-sitemap.xml`;
}

// =============================================================================
// Absolute URL helper — make a relative URL absolute (for schema images, etc.)
// =============================================================================

/**
 * Convert a possibly-relative URL into an absolute one. Used in Schema
 * generators where validators reject relative URLs.
 *
 *   absoluteUrl('https://x.com/y') → 'https://x.com/y' (unchanged)
 *   absoluteUrl('//x.com/y')       → 'https://x.com/y'
 *   absoluteUrl('/uploads/x.jpg')  → '{origin}/uploads/x.jpg'
 *   absoluteUrl('uploads/x.jpg')   → '{origin}/uploads/x.jpg'
 */
export function absoluteUrl(url: string): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `${canonicalOrigin()}${url.startsWith('/') ? '' : '/'}${url}`;
}
